import React, { useState, useEffect, useMemo } from "react";
import { CheckCircle, Clock, Building2, Users2, Printer, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trpcClient } from "@/lib/trpc";
import { formatMoney, cn } from "@/lib/utils";
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import type { FilterField } from "@/components/shared/FilterBar";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { useRowSelection } from "@/hooks/useRowSelection";
import { BulkActionBar, type BulkAction } from "@/components/shared/BulkActionBar";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { useConfirm } from "@/providers/ConfirmProvider";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ActionGuard } from "@/components/shared/ActionGuard";
import { ARReconciliationDetailModal } from "./components/ARReconciliationDetailModal";
import CustomerDebtSummaryPrintModal from "@/components/finance/print/CustomerDebtSummaryPrintModal";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import { useExcelExport } from "@/hooks/useExcelExport";
import { downloadExcelTemplate, type ExcelColumnDef } from "@/lib/excel.utils";
import { getStatusOptions } from "@/config/status-registry";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { emsTabClass } from "@/components/ui/ems-tabs";

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  {
    key: "period",
    label: "Kỳ Đối Soát",
    width: 16,
    formatter: (_v, item: any) => item.period || `Tháng ${item.month}/${item.year}`,
  },
  {
    key: "partnerCode",
    label: "Mã Đối Tác",
    width: 16,
    formatter: (_v, item: any) => item.partnerCode || item.customer?.code || item.vendor?.code || "",
  },
  {
    key: "partnerName",
    label: "Tên Đối Tác",
    width: 30,
    formatter: (_v, item: any) => item.partnerName || item.customer?.name || item.vendor?.name || "",
  },
  { key: "openingBalance", label: "Dư Nợ Đầu Kỳ", width: 18, format: "currency" },
  { key: "periodIncrease", label: "Phát Sinh Tăng", width: 18, format: "currency" },
  { key: "periodDecrease", label: "Đã Thanh Toán", width: 18, format: "currency" },
  { key: "closingBalance", label: "Dư Nợ Cuối Kỳ", width: 18, format: "currency" },
  { key: "status", label: "Trạng Thái", width: 16 },
];

const FILTER_FIELDS: FilterField[] = [
  {
    key: "search",
    label: "Tìm kiếm",
    type: "text",
    placeholder: "Mã, Tên đối tác...",
  },
  {
    key: "status",
    label: "Trạng thái",
    type: "combobox-multi",
    options: getStatusOptions("ARReconciliationStatus", false),
  },
  {
    key: "month",
    label: "Tháng",
    type: "text",
    placeholder: "Tháng (1-12)",
  },
  {
    key: "year",
    label: "Năm",
    type: "text",
    placeholder: "Năm",
  },
];

export default function ARReconciliationListPage() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const isAP = location.pathname.includes("/finance/ap");
  const [partnerType, setPartnerType] = useState<"CUSTOMER" | "VENDOR">(isAP ? "VENDOR" : "CUSTOMER");
  const [selectedReconciliationId, setSelectedReconciliationId] = useState<string | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showSummaryReportModal, setShowSummaryReportModal] = useState(false);

  useEffect(() => {
    if (location.pathname.includes("/finance/ap")) {
      setPartnerType("VENDOR");
    } else if (location.pathname.includes("/finance/ar")) {
      setPartnerType("CUSTOMER");
    }
  }, [location.pathname]);

  const {
    paginatedItems,
    isLoading,
    filterValues,
    showFilter,
    currentPage,
    pageSize,
    totalItems,
    filtered,
    onPageChange,
    onPageSizeChange,
    onToggleFilter,
    onClearAll,
  } = useServerListFilter<any>({
    queryKey: partnerType === "CUSTOMER" ? "ar-reconciliations" : "ap-reconciliations",
    queryFn: (params: any) =>
      partnerType === "CUSTOMER"
        ? (trpcClient.ar.getDebtReconciliations.query({
            ...params,
            status: Array.isArray(params.status) ? params.status[0] : params.status,
            month: params.month ? Number(params.month) : undefined,
            year: params.year ? Number(params.year) : undefined,
          }) as any)
        : (trpcClient.ap.getVendorDebtReconciliations.query({
            ...params,
            status: Array.isArray(params.status) ? params.status[0] : params.status,
            month: params.month ? Number(params.month) : undefined,
            year: params.year ? Number(params.year) : undefined,
          }) as any),
  });

  const confirmMutation = useMutation({
    mutationFn: (id: string) =>
      trpcClient.ar.confirmDebtReconciliation.mutate({ id }),
    onSuccess: () => {
      toast.success("Đã xác nhận biên bản đối soát");
      queryClient.invalidateQueries({ queryKey: ["ar-reconciliations"] });
    },
    onError: (error) => {
      toast.error(error.message || "Không thể xác nhận");
    },
  });

  const queryReconciliations = React.useCallback(
    async (params: any) => {
      const qParams = {
        ...params,
        status: Array.isArray(params.status) ? params.status[0] : params.status,
        month: params.month ? Number(params.month) : undefined,
        year: params.year ? Number(params.year) : undefined,
      };
      if (partnerType === "CUSTOMER") {
        return trpcClient.ar.getDebtReconciliations.query(qParams);
      }
      return trpcClient.ap.getVendorDebtReconciliations.query(qParams);
    },
    [partnerType]
  );

  const { hasPermission } = useRoleAccess();
  const confirm = useConfirm();
  const rowIds = useMemo(() => filtered.map((r: any) => r.id).filter(Boolean), [filtered]);
  const rowSelection = useRowSelection(rowIds);
  const { selectedCount, selectedIds, clearSelection } = rowSelection;

  const { handleExport, isExporting } = useExcelExport({
    columns: EXPORT_COLUMNS,
    filename: `doi_soat_cong_no_${partnerType === "CUSTOMER" ? "khach_hang" : "ncc"}`,
    queryFn: queryReconciliations,
    currentFilters: filterValues,
    selectedIds,
  });

  const bulkActions: BulkAction[] = [
    {
      icon: <Download className="h-4 w-4" />,
      label: `Xuất Excel ${selectedCount} dòng`,
      onClick: () => handleExport(),
    },
  ];
  if (partnerType === "CUSTOMER" && hasPermission("finance", "approve")) {
    bulkActions.push({
      icon: <CheckCircle className="h-4 w-4" />,
      label: "Chốt sổ",
      onClick: () => {
        const ids = filtered
          .filter((r: any) => selectedIds.has(r.id) && r.status !== "CONFIRMED")
          .map((r: any) => r.id);
        if (!ids.length) {
          toast.warning("Các kỳ đã chọn đều đã chốt sổ");
          return;
        }
        executeBulkAction({
          confirmFn: confirm,
          selectedIds: ids,
          actionFn: async (id: string) => trpcClient.ar.confirmDebtReconciliation.mutate({ id }),
          actionName: "chốt sổ",
          itemType: "kỳ đối soát",
          requireConfirm: true,
          confirmMessage: `Chốt sổ ${ids.length} kỳ đối soát đã chọn?`,
          onSuccess: () => {
            clearSelection();
            queryClient.invalidateQueries({ queryKey: ["ar-reconciliations"] });
          },
        });
      },
    });
  }

  return (
    <>
      <ListPageShell
        unitLabel="Biên bản đối soát"
        totalItems={totalItems}
        onToggleFilter={onToggleFilter}
        onClearAll={onClearAll}
        showFilter={showFilter}
        filterFields={FILTER_FIELDS}
        filterValues={filterValues}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        headerFilters={
          <div role="tablist" aria-label="Đối tượng đối soát" className="flex items-end gap-1">
            <button
              type="button"
              role="tab"
              aria-selected={partnerType === "CUSTOMER"}
              className={emsTabClass(partnerType === "CUSTOMER", { size: "sm" })}
              onClick={() => {
                setPartnerType("CUSTOMER");
                if (location.pathname.includes("/finance/ap")) {
                  navigate({ to: "/finance/ar/reconciliations" as any });
                }
              }}
            >
              <Users2 className="w-3.5 h-3.5" />
              Khách Hàng (Phải Thu AR)
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={partnerType === "VENDOR"}
              className={emsTabClass(partnerType === "VENDOR", { size: "sm" })}
              onClick={() => {
                setPartnerType("VENDOR");
                if (location.pathname.includes("/finance/ar")) {
                  navigate({ to: "/finance/ap/reconciliations" as any });
                }
              }}
            >
              <Building2 className="w-3.5 h-3.5" />
              Nhà Cung Cấp (Phải Trả AP)
            </button>
          </div>
        }
        actions={
          <div className="flex gap-2">
            {partnerType === "CUSTOMER" && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs bg-white border-blue-200 text-blue-700 hover:bg-blue-50 shadow-xs"
                onClick={() => setShowSummaryReportModal(true)}
              >
                <Printer className="mr-1.5 h-3.5 w-3.5 text-blue-600" /> In Công Nợ
              </Button>
            )}
            <ExportImportButtons
              onExport={() => handleExport()}
              exportLoading={isExporting}
              onDownloadTemplate={() => downloadExcelTemplate(EXPORT_COLUMNS, "mau_doi_soat_cong_no")}
            />
          </div>
        }
      >
        <div className="overflow-x-auto min-h-[50vh]">
          <table
            className={cn("w-full text-xs caption-bottom apv-compact-table")}
          >
            <thead>
              <tr className="border-b bg-slate-50/50">
                <DataTableSelectAllCheckbox rowSelection={rowSelection} />
                <th className="px-3 py-2 text-center font-medium text-slate-500 w-[60px]">
                  STT
                </th>
                <th className="px-3 py-2 text-left font-medium text-slate-500 w-[120px]">
                  Kỳ đối soát
                </th>
                <th className="px-3 py-2 text-left font-medium text-slate-500 min-w-[200px]">
                  {partnerType === "CUSTOMER" ? "Khách Hàng" : "Nhà Cung Cấp"}
                </th>
                <th className="px-3 py-2 text-right font-medium text-slate-500 w-[140px]">
                  Dư nợ đầu kỳ
                </th>
                <th className="px-3 py-2 text-right font-medium text-slate-500 w-[140px]">
                  {partnerType === "CUSTOMER" ? "Giao hàng (+)" : "Mua hàng/Gia công (+)"}
                </th>
                <th className="px-3 py-2 text-right font-medium text-slate-500 w-[140px]">
                  {partnerType === "CUSTOMER" ? "Đã thu tiền (-)" : "Đã thanh toán (-)"}
                </th>
                <th className="px-3 py-2 text-right font-medium text-slate-500 w-[140px]">
                  Dư nợ cuối kỳ
                </th>
                <th className="px-3 py-2 text-center font-medium text-slate-500 w-[120px]">
                  Trạng thái
                </th>
                <th className="px-3 py-2 text-center font-medium text-slate-500 w-[120px]">
                  Hành động
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                Array(5)
                  .fill(0)
                  .map((_, i) => (
                    <tr key={i}>
                      <td colSpan={10} className="p-3">
                        <div className="h-6 bg-slate-100 rounded animate-pulse" />
                      </td>
                    </tr>
                  ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-slate-400">
                    Không tìm thấy dữ liệu đối soát {partnerType === "CUSTOMER" ? "Khách hàng" : "Nhà cung cấp"}
                  </td>
                </tr>
              ) : (
                filtered.map((item: any, idx: number) => {
                  const initialBalance = Number(item.openingBalance || item.initialBalance || item.initialDebt || 0);
                  const totalDebit = Number(item.totalIncurred || item.totalDebit || item.orderValue || 0);
                  const totalCredit = Number(item.totalPaid || item.totalCredit || item.paidAmount || 0);
                  const finalBalance = partnerType === "CUSTOMER" 
                    ? Number(item.closingBalance || item.finalBalance || (initialBalance + totalDebit - totalCredit))
                    : Number(item._currentDebt || (initialBalance + totalDebit - totalCredit));
                  const partnerName = item.customer?.name || item.vendor?.name || item.partnerName || item.name || "-";
                  const partnerCode = item.customer?.code || item.vendor?.code || item.partnerCode || item.code;
                  // Sổ công nợ NCC nhận id hoặc mã NCC (getAPLedger tìm theo cả hai)
                  const vendorKey = item.vendorId || item.vendor?.id || item.vendor?.code;

                  return (
                    <DataTableRow
                      key={item.id || idx}
                      id={item.id || String(idx)}
                      rowSelection={rowSelection}
                      className="hover:bg-blue-50/40 transition-colors group"
                      onClick={() => {
                        if (partnerType === "CUSTOMER") {
                          setSelectedReconciliationId(item.id);
                          setShowDetailModal(true);
                        } else if (vendorKey) {
                          navigate({ to: "/finance/ap/ledger/$id" as any, params: { id: vendorKey } as any });
                        }
                      }}
                    >
                      <DataTableRowCheckbox id={item.id || String(idx)} rowSelection={rowSelection} />
                      <td className="px-2 py-2 text-center">
                        <span className="text-slate-400">
                          {(currentPage - 1) * pageSize + idx + 1}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-medium text-slate-700">
                        {item.month && item.year ? `Tháng ${item.month}/${item.year}` : "Tháng hiện hành"}
                      </td>
                      <td className="px-3 py-2 text-slate-700 font-medium apv-col-code">
                        {partnerCode && <span className="font-semibold text-slate-900 mr-1.5">{partnerCode}</span>}
                        <span>{partnerName}</span>
                      </td>
                      <td className="px-3 py-2 text-right text-slate-600">
                        {formatMoney(initialBalance)}
                      </td>
                      <td className="px-3 py-2 text-right text-rose-600">
                        {formatMoney(totalDebit)}
                      </td>
                      <td className="px-3 py-2 text-right text-emerald-600">
                        {formatMoney(totalCredit)}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-bold ${finalBalance > 0 ? "text-rose-600" : "text-emerald-600"}`}
                      >
                        {formatMoney(finalBalance)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {item.status === "CONFIRMED" ? (
                          <span className="inline-flex items-center text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full border border-emerald-200">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Đã xác nhận
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-[11px] font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded-full border border-amber-200">
                            <Clock className="w-3 h-3 mr-1" />
                            Chờ xác nhận
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center apv-col-actions" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100"
                            onClick={() => {
                              if (partnerType === "CUSTOMER") {
                                window.open(`/finance/ar/reconciliations/${item.id}/print`, '_blank');
                              } else {
                                window.open(`/finance/ap/reconciliations/${item.id}/print`, '_blank');
                              }
                            }}
                          >
                            <Printer className="w-3.5 h-3.5 mr-1" />
                            In BB
                          </Button>
                          {partnerType === "CUSTOMER" && item.status !== "CONFIRMED" && (
                            <ActionGuard
                              module="finance"
                              action="approve"
                              fallbackMessage="Chỉ Kế Toán Trưởng hoặc Giám Đốc mới có quyền Chốt Sổ Kỳ Đối Soát Công Nợ"
                            >
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                disabled={confirmMutation.isPending}
                                onClick={() => confirmMutation.mutate(item.id)}
                              >
                                Chốt sổ
                              </Button>
                            </ActionGuard>
                          )}
                        </div>
                      </td>
                    </DataTableRow>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </ListPageShell>

      <ARReconciliationDetailModal
        reconciliationId={selectedReconciliationId}
        open={showDetailModal}
        onOpenChange={setShowDetailModal}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["ar-reconciliations"] });
        }}
      />
      <CustomerDebtSummaryPrintModal
        open={showSummaryReportModal}
        onClose={() => setShowSummaryReportModal(false)}
      />
      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={bulkActions}
      />
    </>
  );
}
